// src/pages/ContentPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import Layout from '../Layout/Layout';
import PaymentModal from '../components/PaymentModal';
import api from '../utils/apiClient'; 
import {
    FiArrowLeft,
    FiArrowRight,
    FiHome,
    FiClock,
    FiCheck,
    FiBook,
    FiPlay,
    FiFile,
    FiExternalLink,
    FiDownload,
    FiFileText,
    FiAlertCircle,
    FiRefreshCw,
    FiShield,
    FiLock
} from 'react-icons/fi';

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const NavigationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  a {
    color: ${({ theme }) => theme.colors.textSecondary};
    transition: color ${({ theme }) => theme.transitions.fast};
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
  
  span.separator {
    margin: 0 ${({ theme }) => theme.spacing[2]};
  }
`;

const NavButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const NavButton = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
    transform: translateY(-2px);
  }
  
  svg {
    ${({ prev, theme }) => prev ? `margin-right: ${theme.spacing[2]}` : `margin-left: ${theme.spacing[2]}`};
  }
  
  ${({ disabled, theme }) => disabled && `
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const ContentHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const ModuleTitle = styled.h2`
  font-size: ${({ theme }) => theme.fonts.sizes.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const ContentTitle = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ContentMeta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const ContentType = styled.div`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  background-color: ${({ theme, type }) => {
        switch (type) {
            case 'video': return `${theme.colors.info}20`;
            case 'text': return `${theme.colors.primary}20`;
            case 'interactive': return `${theme.colors.warning}20`;
            case 'external_tool': return `${theme.colors.secondary}20`;
            default: return theme.colors.backgroundLighter;
        }
    }};
  color: ${({ theme, type }) => {
        switch (type) {
            case 'video': return theme.colors.info;
            case 'text': return theme.colors.primary;
            case 'interactive': return theme.colors.warning;
            case 'external_tool': return theme.colors.secondary;
            default: return theme.colors.textSecondary;
        }
    }};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  font-weight: 600;
  text-transform: uppercase;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const ContentMain = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  overflow: hidden;
`;

const Video = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VideoPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  svg {
    font-size: 48px;
    margin-bottom: ${({ theme }) => theme.spacing[2]};
  }
`;

const IframeContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  overflow: hidden;
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ContentText = styled.div`
  line-height: 1.6;
  
  h2 {
    font-size: ${({ theme }) => theme.fonts.sizes.xl};
    margin: ${({ theme }) => `${theme.spacing[6]} 0 ${theme.spacing[3]}`};
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  h3 {
    font-size: ${({ theme }) => theme.fonts.sizes.lg};
    margin: ${({ theme }) => `${theme.spacing[5]} 0 ${theme.spacing[2]}`};
  }
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
  
  ul, ol {
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    padding-left: ${({ theme }) => theme.spacing[6]};
    
    li {
      margin-bottom: ${({ theme }) => theme.spacing[2]};
    }
  }
  
  code {
    font-family: ${({ theme }) => theme.fonts.code};
    background-color: ${({ theme }) => theme.colors.backgroundLighter};
    padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
  
  pre {
    font-family: ${({ theme }) => theme.fonts.code};
    background-color: ${({ theme }) => theme.colors.backgroundLighter};
    padding: ${({ theme }) => theme.spacing[4]};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    overflow-x: auto;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
  
  blockquote {
    border-left: 4px solid ${({ theme }) => theme.colors.primary};
    padding-left: ${({ theme }) => theme.spacing[4]};
    margin-left: 0;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    font-style: italic;
  }
  
  img {
    max-width: 100%;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    
    th, td {
      padding: ${({ theme }) => theme.spacing[3]};
      border: 1px solid ${({ theme }) => theme.colors.divider};
      text-align: left;
    }
    
    th {
      background-color: ${({ theme }) => theme.colors.backgroundLighter};
    }
  }
`;

const SupportMaterials = styled.div`
  margin-top: ${({ theme }) => theme.spacing[6]};
  padding-top: ${({ theme }) => theme.spacing[6]};
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

const SupportMaterialsTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

const SupportMaterialsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
`;

const MaterialItem = styled.a`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    background-color: ${({ theme }) => theme.colors.cardGlow};
  }
  
  svg {
    font-size: 24px;
    margin-right: ${({ theme }) => theme.spacing[3]};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const MaterialInfo = styled.div`
  display: flex;
  flex-direction: column;
  
  .name {
    font-weight: 500;
    margin-bottom: 2px;
  }
  
  .type {
    font-size: ${({ theme }) => theme.fonts.sizes.xs};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const ExternalToolLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }
`;

const MarkAsCompleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  background: ${({ theme, completed }) =>
        completed ? theme.colors.success : theme.colors.gradientPrimary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  transition: all ${({ theme }) => theme.transitions.fast};
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const NextButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary}10;
    transform: translateY(-2px);
  }
  
  svg {
    margin-left: ${({ theme }) => theme.spacing[2]};
  }
`;

const ErrorContainer = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  text-align: center;
`;

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  background-color: ${({ theme }) => theme.colors.error}10;
  border-left: 4px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  
  svg {
    color: ${({ theme }) => theme.colors.error};
    flex-shrink: 0;
    margin-top: 2px;
    font-size: 24px;
  }
  
  div {
    text-align: left;
  }
  
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  
  .error-details {
    margin-top: ${({ theme }) => theme.spacing[2]};
    padding: ${({ theme }) => theme.spacing[2]};
    background-color: ${({ theme }) => theme.colors.backgroundLighter};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-family: ${({ theme }) => theme.fonts.code};
    font-size: ${({ theme }) => theme.fonts.sizes.xs};
    color: ${({ theme }) => theme.colors.textSecondary};
    max-height: 100px;
    overflow-y: auto;
  }
`;

const RetryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  margin: ${({ theme }) => theme.spacing[4]} auto 0;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
  
  svg {
    animation: ${({ loading }) => loading ? 'spin 1s linear infinite' : 'none'};
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[8]};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  
  svg {
    font-size: 36px;
    color: ${({ theme }) => theme.colors.primary};
    animation: spin 1s linear infinite;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
  
  p {
    font-size: ${({ theme }) => theme.fonts.sizes.lg};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PaymentBlockedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

const PaymentBlockedIcon = styled(FiShield)`
  font-size: 4rem;
  color: ${({ theme }) => theme.colors.warning};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const PaymentBlockedTitle = styled.h2`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const PaymentBlockedMessage = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  max-width: 600px;
`;

const PaymentButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fonts.sizes.base};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;

const BackButton = styled(Link)`
  margin-top: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ContentPage = () => {
    const { courseId, contentId } = useParams();
    const navigate = useNavigate();
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorDetails, setErrorDetails] = useState(null);
    const [completed, setCompleted] = useState(false);
    const startTimeRef = useRef(Date.now());
    const [supportMaterials, setSupportMaterials] = useState([]);
    const progressIntervalRef = useRef(null);
    const [savingProgress, setSavingProgress] = useState(false);
    const [markingComplete, setMarkingComplete] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [accessBlocked, setAccessBlocked] = useState(false);
    const [courseInfo, setCourseInfo] = useState(null);

    // Track progress at regular intervals (every 30 seconds)
    const setupProgressTracking = (contentId) => {
        // Clear any existing intervals
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
        }

        // Track progress every 30 seconds
        progressIntervalRef.current = setInterval(() => {
            trackProgress(contentId);
        }, 30000); // 30 seconds
    };

    // Track progress function
    const trackProgress = async (contentId) => {
        try {
            if (savingProgress || !api?.content?.trackProgress) return; // Prevent multiple simultaneous calls

            setSavingProgress(true);
            const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000); // in seconds

            console.log(`Tracking progress for content ${contentId}: ${timeSpent} seconds`);

            // Check if API method exists before calling
            if (typeof api.content.trackProgress === 'function') {
                await api.content.trackProgress(contentId, timeSpent);
            }

            // Reset the start time after tracking
            startTimeRef.current = Date.now();
            setSavingProgress(false);
        } catch (err) {
            console.error("Error tracking progress:", err);
            setSavingProgress(false);
        }
    };

    // Fetch content data
    const fetchContent = async () => {
        try {
            setLoading(true);
            setError(null);
            setErrorDetails(null);

            // Reset start time for tracking
            startTimeRef.current = Date.now();

            // Log the request for debugging
            console.log(`Fetching content: courseId=${courseId}, contentId=${contentId}`);

            let contentData;

            // Check if API is available
            if (api?.content?.getById && typeof api.content.getById === 'function') {
                // **PAYMENT PROTECTION**: First check course access
                try {
                    const courseDetails = await api.courses.getById(courseId);
                    setCourseInfo(courseDetails);
                    
                    // Check if user has access to course content
                    if (!courseDetails.is_free && !courseDetails.has_purchased && !courseDetails.enrolled) {
                        setAccessBlocked(true);
                        setLoading(false);
                        return;
                    }
                } catch (courseError) {
                    console.warn('Could not verify course access:', courseError);
                    // Continue with content fetch, let content API handle access control
                }

                // Get content data from API
                contentData = await api.content.getById(contentId);
                console.log("API response:", contentData);
            } else {
                // Fallback to mock data when API isn't available (for development/testing)
                console.log("API not available, using fallback data");
                contentData = {
                    id: parseInt(contentId),
                    title: 'Configuring Firewall Rules',
                    content_type: 'text',
                    duration: '30 min',
                    is_completed: false,
                    module: {
                        id: 2,
                        number: 2,
                        title: 'Firewall Implementation'
                    },
                    course: {
                        id: parseInt(courseId),
                        title: 'Advanced Network Security'
                    },
                    content: `
                      <h2>Understanding Firewall Rules</h2>
                      <p>Firewall rules define how traffic is allowed or denied through a network security system. Properly configured firewall rules are essential to maintaining a secure network perimeter.</p>
                      
                      <p>In this lesson, we'll explore the proper way to configure firewall rules to maximize security while ensuring necessary services remain accessible.</p>
                      
                      <h2>Rule Structure</h2>
                      <p>Most firewall rules consist of the following components:</p>
                      <ul>
                        <li><strong>Source Address</strong>: The origin of the traffic</li>
                        <li><strong>Destination Address</strong>: Where the traffic is headed</li>
                        <li><strong>Protocol</strong>: The protocol being used (TCP, UDP, ICMP, etc.)</li>
                        <li><strong>Source Port</strong>: The port from which the traffic originates</li>
                        <li><strong>Destination Port</strong>: The port to which the traffic is headed</li>
                        <li><strong>Action</strong>: What to do with matching traffic (allow, deny, log)</li>
                      </ul>
                    `
                };

                // Check if we should mark this as completed from localStorage
                const completedContents = JSON.parse(localStorage.getItem('completedContents') || '[]');
                contentData.is_completed = completedContents.includes(parseInt(contentId));

                // Add navigation data if not present
                if (!contentData.prev_content) {
                    contentData.prev_content = { id: parseInt(contentId) - 1, title: 'Previous Lesson' };
                }
                if (!contentData.next_content) {
                    contentData.next_content = { id: parseInt(contentId) + 1, title: 'Next Lesson' };
                }
            }

            // If we have a valid response
            if (contentData) {
                setContent(contentData);
                setCompleted(contentData.is_completed || false);

                // Setup progress tracking
                setupProgressTracking(contentId);

                // Merge support materials and any standalone content.file_url into a single list
                const existingMaterials = Array.isArray(contentData.support_materials) ? [...contentData.support_materials] : [];

                // Normalize helpers to check for duplicate urls
                const materialUrls = new Set(existingMaterials.map(m => m.url || m.file_url || m.download_url));

                if (contentData.file_url && !materialUrls.has(contentData.file_url)) {
                    existingMaterials.push({
                        id: `file-${contentData.id || '0'}`,
                        url: contentData.file_url,
                        name: 'Download Content File',
                        type: 'File'
                    });
                }

                setSupportMaterials(existingMaterials);
            } else {
                // Handle empty response
                setError("Received empty response from server");
                setErrorDetails(JSON.stringify(contentData));
            }

            setLoading(false);
            setRetrying(false);
        } catch (err) {
            console.error("Error fetching content:", err);

            // **PAYMENT PROTECTION**: Handle payment required errors
            if (err.response?.status === 402 || err.response?.data?.requires_payment) {
                setAccessBlocked(true);
                toast.error('Payment required to access course content.');
                setLoading(false);
                return;
            } else if (err.response?.status === 403) {
                toast.error('You do not have access to this course content.');
                navigate('/courses');
                return;
            }

            // Create detailed error message
            setError("Failed to load content. Please try again.");

            // Store error details for debugging
            const errorInfo = {
                message: err.message,
                status: err.response?.status,
                data: err.response?.data,
                url: err.config?.url
            };
            setErrorDetails(JSON.stringify(errorInfo, null, 2));

            setLoading(false);
            setRetrying(false);
        }
    };

    // Use effect to fetch content on initial load or when contentId changes
    useEffect(() => {
        fetchContent();

        // Cleanup: track final progress and clear interval when unmounting
        return () => {
            // Track final progress before leaving
            if (contentId && !error && api?.content?.trackProgress) {
                trackProgress(contentId);
            }

            // Clear interval
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, [contentId, courseId, error]);

    // Mark content as complete
    const handleMarkAsComplete = async () => {
        try {
            setMarkingComplete(true);

            console.log(`Marking content ${contentId} as complete`);

            // Check if API method exists
            if (api?.content?.markComplete && typeof api.content.markComplete === 'function') {
                // Mark content as completed via API
                const response = await api.content.markComplete(contentId);
                console.log("Mark complete response:", response);
            } else {
                // Fallback for when API is not available - use localStorage
                const completedContents = JSON.parse(localStorage.getItem('completedContents') || '[]');
                if (!completedContents.includes(parseInt(contentId))) {
                    completedContents.push(parseInt(contentId));
                    localStorage.setItem('completedContents', JSON.stringify(completedContents));
                }
                console.log(`Marked content ${contentId} as complete in localStorage`);
            }

            // Update local state
            setCompleted(true);
            setMarkingComplete(false);
        } catch (err) {
            console.error("Error marking content as complete:", err);
            setError("Failed to mark content as complete. Please try again.");

            // Store error details for debugging
            const errorInfo = {
                message: err.message,
                status: err.response?.status,
                data: err.response?.data,
                url: err.config?.url
            };
            setErrorDetails(JSON.stringify(errorInfo, null, 2));

            setMarkingComplete(false);
        }
    };

    // Handle retry
    const handleRetry = () => {
        setRetrying(true);
        fetchContent();
    };

    if (loading) {
        return (
            <Layout>
                <LoadingContainer>
                    <FiRefreshCw />
                    <p>Loading content...</p>
                </LoadingContainer>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <ErrorContainer>
                    <ErrorMessage>
                        <FiAlertCircle />
                        <div>
                            <p>{error}</p>
                            {errorDetails && (
                                <pre className="error-details">{errorDetails}</pre>
                            )}
                        </div>
                    </ErrorMessage>

                    <RetryButton onClick={handleRetry} loading={retrying}>
                        {retrying ? (
                            <>
                                <FiRefreshCw />
                                Retrying...
                            </>
                        ) : (
                            <>
                                <FiRefreshCw />
                                Retry
                            </>
                        )}
                    </RetryButton>
                </ErrorContainer>
            </Layout>
        );
    }

    // If we somehow get here without content, show an error
    if (!content) {
        return (
            <Layout>
                <ErrorContainer>
                    <ErrorMessage>
                        <FiAlertCircle />
                        <div>
                            <p>No content data found. Please try again or contact support.</p>
                        </div>
                    </ErrorMessage>

                    <RetryButton onClick={handleRetry} loading={retrying}>
                        {retrying ? (
                            <>
                                <FiRefreshCw />
                                Retrying...
                            </>
                        ) : (
                            <>
                                <FiRefreshCw />
                                Retry
                            </>
                        )}
                    </RetryButton>
                </ErrorContainer>
            </Layout>
        );
    }

    const getContentTypeIcon = (type) => {
        switch (type) {
            case 'video':
                return <FiPlay />;
            case 'text':
                return <FiFileText />;
            case 'interactive':
                return <FiPlay />;
            case 'external_tool':
                return <FiExternalLink />;
            default:
                return <FiFileText />;
        }
    };

    const isYouTubeUrl = (url) => {
        if (!url) return false;
        return /youtube\.com\/watch|youtube\.com\/embed|youtu\.be\//.test(url);
    };

    // YouTube embed URL extraction
    const getYouTubeEmbedUrl = (url) => {
        // Return if already an embed URL
        if (url.includes('youtube.com/embed/')) {
            return url.replace('youtube.com', 'youtube-nocookie.com');
        }

        // Extract video ID from different YouTube URL formats
        let videoId = '';

        if (url.includes('youtube.com/watch')) {
            // Handle youtube.com/watch?v=VIDEO_ID format
            const urlParams = new URLSearchParams(new URL(url).search);
            videoId = urlParams.get('v');
        } else if (url.includes('youtu.be/')) {
            // Handle youtu.be/VIDEO_ID format
            videoId = url.split('youtu.be/')[1];
        }

        // Remove any additional parameters
        if (videoId && videoId.includes('&')) {
            videoId = videoId.split('&')[0];
        }

        // Return embed URL with parameters for better embedding
        return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1`;
    };

    // Always show description above main content
    const renderMainContent = () => {
        const desc = content.description || content.desc || '';
        const type = (content.content_type || '').toLowerCase();

        // Show description if available
        const descriptionBlock = desc ? (
            <div style={{ marginBottom: 24 }}>
                <ContentText>{desc}</ContentText>
            </div>
        ) : null;

        // Handle all supported types
        if (type === 'video') {
            return (
                <>
                    {descriptionBlock}
                    <VideoContainer>
                        {content.video_url ? (
                            isYouTubeUrl(content.video_url) ? (
                                <iframe
                                    src={getYouTubeEmbedUrl(content.video_url)}
                                    title={content.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                ></iframe>
                            ) : (
                                <Video controls>
                                    <source src={content.video_url} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </Video>
                            )
                        ) : (
                            <VideoPlaceholder>
                                <FiPlay />
                                <p>Video unavailable or still processing</p>
                            </VideoPlaceholder>
                        )}
                    </VideoContainer>
                </>
            );
        }
        if (type === 'pdf') {
            return (
                <>
                    {descriptionBlock}
                    <IframeContainer>
                        <iframe
                            src={content.pdf_url || content.file_url}
                            title={content.title}
                            allowFullScreen
                        ></iframe>
                    </IframeContainer>
                </>
            );
        }
        if (type === 'lab') {
            return (
                <>
                    {descriptionBlock}
                    <IframeContainer>
                        <iframe
                            src={content.pdf_url || content.file_url}
                            title={content.title}
                            allowFullScreen
                        ></iframe>
                    </IframeContainer>
                </>
            );
        }
        if (type === 'external_tool') {
            return (
                <>
                    {descriptionBlock}
                    <IframeContainer>
                        <iframe
                            src={content.external_tool_url}
                            title={content.title}
                            allowFullScreen
                        ></iframe>
                    </IframeContainer>
                    <ExternalToolLink href={content.external_tool_url} target="_blank" rel="noopener noreferrer">
                        <FiExternalLink />
                        Open in new tab
                    </ExternalToolLink>
                </>
            );
        }
        if (type === 'interactive') {
            return (
                <>
                    {descriptionBlock}
                    <IframeContainer>
                        <iframe
                            src={`/interactive/${content.id}`}
                            title={content.title}
                            allowFullScreen
                        ></iframe>
                    </IframeContainer>
                </>
            );
        }
        if (type === 'text' || type === 'text/markdown') {
            return descriptionBlock;
        }
        // Fallback for unknown types
        return (
            <>
                {descriptionBlock}
                <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: 'var(--background-lighter)',
                    borderRadius: 'var(--border-radius-md)',
                    marginBottom: '24px'
                }}>
                    <FiFileText style={{ fontSize: '32px', marginBottom: '12px' }} />
                    <p>This content is available in {content.content_type} format.</p>
                </div>
            </>
        );
    };

    // Determine URL path based on the route structure
    const getNextContentUrl = () => {
        if (!content.next_content) return null;

        // Check if we're using /courses/ or /learning/ in the URL
        const basePath = window.location.pathname.includes('/learning/')
            ? `/learning/${courseId}/${content.next_content.id}`
            : `/courses/${courseId}/content/${content.next_content.id}`;

        return basePath;
    };

    const getPrevContentUrl = () => {
        if (!content.prev_content) return null;

        // Check if we're using /courses/ or /learning/ in the URL
        const basePath = window.location.pathname.includes('/learning/')
            ? `/learning/${courseId}/${content.prev_content.id}`
            : `/courses/${courseId}/content/${content.prev_content.id}`;

        return basePath;
    };

    // Handle payment success
    const handlePaymentSuccess = async () => {
        try {
            setAccessBlocked(false);
            // Refresh content access
            await fetchContent();
            toast.success('Payment successful! You now have access to the course content.');
        } catch (error) {
            console.error('Error refreshing content after payment:', error);
            toast.success('Payment successful! Please refresh the page to access content.');
        }
    };

    // **PAYMENT PROTECTION**: Show payment required view
    if (accessBlocked && courseInfo) {
        return (
            <Layout>
                <PaymentBlockedContainer>
                    <PaymentBlockedIcon />
                    <PaymentBlockedTitle>Payment Required</PaymentBlockedTitle>
                    <PaymentBlockedMessage>
                        This course requires payment before you can access the learning materials. 
                        Please purchase the course to continue with your learning journey.
                    </PaymentBlockedMessage>
                    <PaymentButton onClick={() => setShowPaymentModal(true)}>
                        Purchase Course (₹{courseInfo.price})
                    </PaymentButton>
                    <BackButton to="/courses">← Back to Courses</BackButton>
                </PaymentBlockedContainer>

                {/* Payment Modal */}
                <PaymentModal
                    isOpen={showPaymentModal}
                    onClose={() => setShowPaymentModal(false)}
                    course={courseInfo}
                    onSuccess={handlePaymentSuccess}
                />
            </Layout>
        );
    }

    return (
        <Layout>
            <ContentContainer>
                <NavigationBar>
                    <NavLinks>
                        <Breadcrumb>
                            <Link to="/"><FiHome /></Link>
                            <span className="separator">/</span>
                            <Link to={`/courses/${courseId}`}>{content.course?.title || 'Course'}</Link>
                            <span className="separator">/</span>
                            <Link to={`/courses/${courseId}/modules`}>
                                Module {content.module?.number || '1'}: {content.module?.title || 'Module'}
                            </Link>
                            <span className="separator">/</span>
                            <span>{content.title}</span>
                        </Breadcrumb>
                    </NavLinks>

                    <NavButtons>
                        {content.prev_content && (
                            <NavButton
                                to={getPrevContentUrl()}
                                prev
                            >
                                <FiArrowLeft />
                                Previous
                            </NavButton>
                        )}

                        {content.next_content && (
                            <NavButton
                                to={getNextContentUrl()}
                            >
                                Next
                                <FiArrowRight />
                            </NavButton>
                        )}
                    </NavButtons>
                </NavigationBar>

                {/* Show "Continue to Next" button at the top if content is completed */}
                {completed && content.next_content && (
                    <NextButton to={getNextContentUrl()}>
                        Continue to Next Lesson
                        <FiArrowRight />
                    </NextButton>
                )}

                <ContentHeader>
                    <ModuleTitle>
                        Module {content.module?.number || '1'}: {content.module?.title || 'Module'}
                    </ModuleTitle>
                    <ContentTitle>{content.title}</ContentTitle>

                    <ContentMeta>
                        <MetaItem>
                            <FiClock />
                            {content.duration || '30 min'}
                        </MetaItem>

                        <ContentType type={content.content_type || 'text'}>
                            {getContentTypeIcon(content.content_type || 'text')}
                            {(content.content_type || 'text').replace('_', ' ')}
                        </ContentType>
                    </ContentMeta>
                </ContentHeader>

                <ContentMain>
                    {/* Main content based on content_type */}
                    {renderMainContent()}

                    {/* Always display text content regardless of content type */}
                    <div className="content-text-wrapper">
                        {content.text_content ? (
                            <ContentText dangerouslySetInnerHTML={{ __html: content.text_content.replace(/\n\n/g, '<br>').replace(/\n/g, '') }} />
                        ) : content.content ? (
                            <ContentText dangerouslySetInnerHTML={{ __html: content.content }} />
                        ) : (
                            <ContentText>No text content available for this lesson.</ContentText>
                        )}
                    </div>

                    {/* Support Materials - only render if materials exist */}
                    {supportMaterials && supportMaterials.length > 0 && (
                        <SupportMaterials>
                            <SupportMaterialsTitle>
                                <FiBook />
                                Support Materials
                            </SupportMaterialsTitle>

                            <SupportMaterialsList>
                                {supportMaterials.map((material, index) => (
                                    <MaterialItem
                                        key={material.id || index}
                                        href={material.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FiFile />
                                        <MaterialInfo>
                                            <span className="name">{material.name}</span>
                                            <span className="type">{material.type || 'File'}</span>
                                        </MaterialInfo>
                                    </MaterialItem>
                                ))}
                            </SupportMaterialsList>
                        </SupportMaterials>
                    )}

                    {/* All downloadable files are shown under Support Materials (no separate Content Files section) */}

                    {/* Mark as complete button */}
                    <MarkAsCompleteButton
                        onClick={handleMarkAsComplete}
                        completed={completed}
                        disabled={completed || markingComplete}
                    >
                        {completed ? (
                            <>
                                <FiCheck />
                                Marked as Complete
                            </>
                        ) : markingComplete ? (
                            'Saving...'
                        ) : (
                            'Mark as Complete'
                        )}
                    </MarkAsCompleteButton>

                    {/* Next content button (also shown when current content is completed) */}
                    {completed && content.next_content && (
                        <NextButton to={getNextContentUrl()}>
                            Continue to Next Lesson
                            <FiArrowRight />
                        </NextButton>
                    )}
                </ContentMain>
            </ContentContainer>

            {/* Payment Modal for content access */}
            {courseInfo && (
                <PaymentModal
                    isOpen={showPaymentModal}
                    onClose={() => setShowPaymentModal(false)}
                    course={courseInfo}
                    onSuccess={handlePaymentSuccess}
                />
            )}
        </Layout>
    );
};

export default ContentPage;